import type { ShopperReservationMode } from '@iheartjane/types';

import { resolveSelectedMode } from './resolveSelectedMode';

// Narrows `modes` to the ones that actually have bookable slots right now.
export const modesWithSlots = (
  modes: readonly ShopperReservationMode[],
  hasSlots: { curbside: boolean; delivery: boolean; pickup: boolean }
): ShopperReservationMode[] => modes.filter((mode) => hasSlots[mode]);

// Where to look for a seed target: the modes that currently have open slots,
// falling back to everything selectable when nothing does. `resolveSelectedMode`
// does the preference work from there — it clamps the shopper's desired mode to
// this space, favouring its pickup-family counterpart before anything else.
const resolveSearchSpace = (
  slotOpenModes: readonly ShopperReservationMode[],
  selectableModes: readonly ShopperReservationMode[]
): readonly ShopperReservationMode[] =>
  slotOpenModes.length ? slotOpenModes : selectableModes;

// The one-time seed decision shared by every fulfillment toggle: where should
// the shopper be moved to, given what has slots? `null` means leave them where
// they are.
export const resolveModeChange = (
  reservationMode: string | undefined,
  pickupSubMode: string | undefined,
  slotOpenModes: readonly ShopperReservationMode[],
  selectableModes: readonly ShopperReservationMode[]
): ShopperReservationMode | null => {
  if (!selectableModes.length) return null;

  const current = resolveSelectedMode(reservationMode, pickupSubMode);

  const searchSpace = resolveSearchSpace(slotOpenModes, selectableModes);
  const target = resolveSelectedMode(
    reservationMode,
    pickupSubMode,
    searchSpace
  );

  if (reservationMode && target === current) return null;

  if (!selectableModes.includes(current)) return target;

  if (!reservationMode) return target;

  if ((target === 'delivery') !== (current === 'delivery')) return null;

  return target;
};
